/**
 * DM-18 — Route `/docs/$role/$slug/edit` (только Админ).
 */

import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { DocsForbidden, DocsLayout, DocsSkeleton } from '~/components/docs';
import { DocsEditor } from '~/components/docs/DocsEditor';
import { useToast } from '~/components/feedback';
import { fetchDocPage } from '~/lib/api/docs';
import { useAuthStore } from '~/store/auth';

export const Route = createFileRoute('/_auth/docs/$role/$slug/edit')({
  component: DocsEditPage,
});

function DocsEditPage(): JSX.Element {
  const params = useParams({ from: '/_auth/docs/$role/$slug/edit' });
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.user?.role ?? 'viewer');
  const toast = useToast();

  const pageQuery = useQuery({
    queryKey: ['docs', 'page', params.role, params.slug, 'ru'],
    queryFn: () => fetchDocPage(params.role, params.slug, { locale: 'ru' }),
  });

  if (userRole !== 'admin') {
    return (
      <DocsLayout toc={undefined} currentRole={params.role} currentSlug={params.slug}>
        <DocsForbidden />
      </DocsLayout>
    );
  }

  return (
    <DocsLayout
      toc={undefined}
      currentRole={params.role}
      currentSlug={params.slug}
      breadcrumbs={[
        { label: 'Документация', href: '/docs' },
        {
          label: pageQuery.data?.title ?? params.slug,
          href: `/docs/${params.role}/${params.slug}`,
        },
        { label: 'Редактирование' },
      ]}
    >
      {pageQuery.isLoading ? (
        <DocsSkeleton />
      ) : pageQuery.data ? (
        <DocsEditor
          page={pageQuery.data}
          role={params.role}
          slug={params.slug}
          onSaved={(newVersion) => {
            toast.push({ variant: 'success', title: `Сохранено как версия ${newVersion}` });
            void navigate({
              to: '/docs/$role/$slug',
              params: { role: params.role, slug: params.slug },
            });
          }}
          onCancel={() =>
            navigate({
              to: '/docs/$role/$slug',
              params: { role: params.role, slug: params.slug },
            })
          }
          onOpenHistory={() => {
            window.location.href = `/docs/${params.role}/${params.slug}/history`;
          }}
        />
      ) : null}
    </DocsLayout>
  );
}
